import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMediaInfinite } from '../api';
import { useGallery } from '../context/GalleryContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import InviteCode from './InviteCode';
import {
  getMediaProcessedPhotoUrl,
  isMediaProcessed,
} from './PhotoGallery/utils/photoHelpers';

const MIN_INTERVAL_SECONDS = 3;
const MAX_INTERVAL_SECONDS = 60;
const DEFAULT_INTERVAL_SECONDS = 8;
const REFRESH_INTERVAL_MS = 60 * 1000;

export const clampSlideshowInterval = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_INTERVAL_SECONDS;
  return Math.min(MAX_INTERVAL_SECONDS, Math.max(MIN_INTERVAL_SECONDS, Math.round(parsed)));
};

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
}

export function shouldIgnoreSlideshowShortcut(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('input, button, select, textarea')
    || target.closest('[contenteditable]:not([contenteditable="false"])')
  );
}

export default function GallerySlideshow() {
  const {
    isAuthenticated,
    setIsAuthenticated,
    logoUrl,
    authFailure,
  } = useOutletContext();
  const { galleryId } = useGallery();
  const { isOnline } = useNetworkStatus();
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [isPlaying, setIsPlaying] = useState(() => !prefersReducedMotion());
  const [intervalSeconds, setIntervalSeconds] = useState(DEFAULT_INTERVAL_SECONDS);
  const [currentIndex, setCurrentIndex] = useState(0);

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useMediaInfinite(30, 'image', {
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated && isOnline ? REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

  const images = useMemo(
    () => (data?.pages?.flatMap((page) => page.items) || [])
      .filter(isMediaProcessed)
      .map((item) => ({
        url: getMediaProcessedPhotoUrl(item, galleryId),
      }))
      .filter(({ url }) => Boolean(url)),
    [data?.pages, galleryId]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return undefined;
    const handleChange = (event) => {
      setReducedMotion(event.matches);
      if (event.matches) setIsPlaying(false);
    };
    mediaQuery.addEventListener?.('change', handleChange);
    return () => mediaQuery.removeEventListener?.('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isPlaying || images.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setCurrentIndex((index) => {
        if (index + 1 < images.length) return index + 1;
        return 0;
      });
    }, intervalSeconds * 1000);
    return () => window.clearInterval(timer);
  }, [images.length, intervalSeconds, isPlaying]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || currentIndex < images.length - 3) return;
    Promise.resolve(fetchNextPage()).catch(() => undefined);
  }, [currentIndex, fetchNextPage, hasNextPage, images.length, isFetchingNextPage]);

  useEffect(() => {
    if (images.length === 0) return;
    const preloadUrls = [...new Set([1, 2]
      .map((offset) => (currentIndex + offset) % images.length)
      .filter((index) => index !== currentIndex)
      .map((index) => images[index]?.url)
      .filter(Boolean))];
    preloadUrls.forEach((url) => {
      const preload = new Image();
      preload.src = url;
    });
  }, [currentIndex, images]);

  useEffect(() => {
    if (currentIndex < images.length || images.length === 0) return;
    setCurrentIndex(images.length - 1);
  }, [currentIndex, images.length]);

  const togglePlayback = useCallback(() => setIsPlaying((playing) => !playing), []);
  const showPrevious = useCallback(() => {
    setIsPlaying(false);
    setCurrentIndex((index) => (index === 0 ? Math.max(images.length - 1, 0) : index - 1));
  }, [images.length]);
  const showNext = useCallback(() => {
    setIsPlaying(false);
    setCurrentIndex((index) => (index + 1 < images.length ? index + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const handleKeyDown = (event) => {
      if (shouldIgnoreSlideshowShortcut(event.target)) return;
      if (event.key === ' ') {
        event.preventDefault();
        togglePlayback();
      } else if (event.key === 'ArrowLeft') {
        showPrevious();
      } else if (event.key === 'ArrowRight') {
        showNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated, showNext, showPrevious, togglePlayback]);

  if (!isAuthenticated) {
    return (
      <InviteCode
        setAuth={setIsAuthenticated}
        logo={logoUrl}
        authFailureReason={authFailure?.reason}
      />
    );
  }

  if (isLoading && images.length === 0) {
    return <SlideshowMessage title="Preparing slideshow" detail="Loading wedding photos…" />;
  }

  if (images.length === 0) {
    if (!isOnline) {
      return (
        <SlideshowMessage
          title="You’re offline"
          detail="Reconnect to load photos for this slideshow."
        />
      );
    }
    if (error) {
      return (
        <SlideshowMessage title="Couldn’t load slideshow" detail="Try again in a moment.">
          <button type="button" className="px-4 py-2 rounded-full bg-white text-black" onClick={() => refetch()}>
            Try again
          </button>
        </SlideshowMessage>
      );
    }
    return (
      <SlideshowMessage
        title="No photos yet"
        detail="New processed photos will appear here when guests start sharing."
      />
    );
  }

  const currentImage = images[currentIndex] || images[0];
  const statusMessage = !isOnline
    ? 'Offline — showing saved photos'
    : error
      ? 'Could not refresh — showing saved photos'
      : null;

  return (
    <main className="relative min-h-screen bg-black overflow-hidden text-white" aria-label="Wedding photo slideshow">
      <button
        type="button"
        aria-label={isPlaying ? 'Pause slideshow' : 'Resume slideshow'}
        className="absolute inset-0 w-full h-full focus:outline-none"
        onClick={togglePlayback}
      >
        <img
          src={currentImage.url}
          alt={`Wedding memory ${currentIndex + 1} of ${images.length}`}
          className={`w-full h-screen object-contain ${reducedMotion ? '' : 'transition-opacity duration-500'}`}
        />
      </button>

      {statusMessage ? (
        <div role="status" className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm">
          {statusMessage}
        </div>
      ) : null}

      <div className="absolute bottom-0 inset-x-0 flex flex-wrap items-center justify-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-5 pt-12">
        <button type="button" className="rounded-full bg-white/15 px-4 py-2" onClick={showPrevious}>
          Previous
        </button>
        <button type="button" className="rounded-full bg-white px-5 py-2 font-semibold text-black" onClick={togglePlayback}>
          {isPlaying ? 'Pause' : 'Resume'}
        </button>
        <button type="button" className="rounded-full bg-white/15 px-4 py-2" onClick={showNext}>
          Next
        </button>
        <label className="flex items-center gap-2 text-sm">
          Interval
          <input
            aria-label="Slideshow interval in seconds"
            className="w-16 rounded bg-white/15 px-2 py-2 text-white"
            type="number"
            min={MIN_INTERVAL_SECONDS}
            max={MAX_INTERVAL_SECONDS}
            value={intervalSeconds}
            onChange={(event) => {
              setIntervalSeconds(clampSlideshowInterval(event.target.value));
              setIsPlaying(false);
            }}
          />
          seconds
        </label>
        <span aria-live="polite" className="min-w-16 text-center text-sm text-white/80">
          {currentIndex + 1} / {images.length}{isFetchingNextPage ? ' · Loading more…' : ''}
        </span>
      </div>
    </main>
  );
}

function SlideshowMessage({ title, detail, children }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-black p-6 text-center text-white">
      <h1 className="text-2xl font-serif">{title}</h1>
      <p className="max-w-md text-sm text-white/70">{detail}</p>
      {children}
    </main>
  );
}
